/**
 * Generation Progress Component
 * Shows real-time status of video generation
 */

import { useTranslation } from "react-i18next"

import { CheckCircle, Clock, XCircle } from "@/components/icons"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Spinner } from "@/components/ui/spinner"
import { useStudioImageThumbnail } from "@/features/studio-ai/hooks/use-studio-image-thumbnail"
import { useStudioAIStore } from "@/stores/studio-ai-store"

interface GenerationProgressProps {
  jobId?: string
  showEstimate?: boolean
}

interface StatusConfig {
  icon: React.ReactNode
  message: string
  borderColor: string
  spinning?: boolean
}

export function GenerationProgress({ jobId, showEstimate = true }: GenerationProgressProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.progress" })
  const generationState = useStudioAIStore((state) => state.generationState)

  // partialImageUrl during streaming is a data: URL (img-safe) — but the
  // moment generation completes the unified-generation-form swaps it for
  // result.image_url, which is /api/studio-ai/images/<id> and 401s. Route
  // through the resolver so the final frame stays visible.
  const previewThumb = useStudioImageThumbnail(
    generationState.currentJobId ?? undefined,
    generationState.partialImageUrl
  )

  if (!generationState.isGenerating && !generationState.currentJobId) {
    return null
  }

  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  const getStatusConfig = (status: string | undefined): StatusConfig => {
    if (!status) {
      return {
        icon: <Spinner className="h-4 w-4" />,
        message: t("processing"),
        borderColor: "border-l-primary",
      }
    }

    const statusStr = typeof status === "string" ? status : String(status)
    const statusLower = statusStr.toLowerCase()

    switch (statusLower) {
      case "queued":
        return {
          icon: <Clock className="h-4 w-4" />,
          message: t("queued"),
          borderColor: "border-l-border",
        }
      case "preprocessing":
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: t("preprocessing"),
          borderColor: "border-l-primary",
        }
      case "processing":
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: t("generatingVideo"),
          borderColor: "border-l-primary",
        }
      case "starting":
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: t("startingImage", { defaultValue: "Starting image generation..." }),
          borderColor: "border-l-primary",
        }
      case "generating":
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: t("generatingImage", { defaultValue: "Generating image..." }),
          borderColor: "border-l-primary",
        }
      case "editing":
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: t("editingImage", { defaultValue: "Editing image..." }),
          borderColor: "border-l-primary",
        }
      case "running":
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: t("creatingVideo"),
          borderColor: "border-l-primary",
        }
      case "completed":
      case "succeeded":
        return {
          icon: <CheckCircle className="text-success h-4 w-4" />,
          message: t("complete"),
          borderColor: "border-l-success",
        }
      case "failed":
        return {
          icon: <XCircle className="text-destructive h-4 w-4" />,
          message: t("failed"),
          borderColor: "border-l-destructive",
        }
      default:
        return {
          icon: <Spinner className="h-4 w-4" />,
          message: status,
          borderColor: "border-l-primary",
        }
    }
  }

  const statusConfig = getStatusConfig(generationState.status)

  return (
    <Card className={`border-l-4 ${statusConfig.borderColor}`}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {statusConfig.icon}
          {statusConfig.message}
          {typeof generationState.progress === "number" && generationState.progress > 0 && (
            <span className="text-muted-foreground ml-auto text-xs font-normal">
              {generationState.progress}%
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Live partial-image preview while streaming. Same slot the final
            image lands in once status === completed, so the UI doesn't pop. */}
        {previewThumb.url && !previewThumb.error && (
          <div className="relative overflow-hidden rounded-lg">
            <img
              alt={t("generatingPreviewAlt", { defaultValue: "Generating preview" })}
              className="w-full rounded-lg object-cover"
              src={previewThumb.url}
              onError={previewThumb.onImgError}
            />
            {generationState.isGenerating && (
              <div className="pointer-events-none absolute right-0 bottom-0 left-0 bg-black/30">
                <Progress
                  className="h-1 rounded-none bg-transparent"
                  indicatorClassName="rounded-full bg-white/90 duration-500 ease-out"
                  value={generationState.progress || 0}
                />
              </div>
            )}
          </div>
        )}

        {!previewThumb.url && generationState.isGenerating && (
          <div className="bg-muted h-1 overflow-hidden rounded-full">
            <div className="bg-primary-ink h-full w-1/3 animate-pulse rounded-full" />
          </div>
        )}

        {showEstimate && generationState.estimatedTime && (
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <Clock className="h-3 w-3" />
            <span>
              {t("estimatedRemaining", { time: formatTime(generationState.estimatedTime) })}
            </span>
          </div>
        )}

        {jobId && (
          <p className="text-muted-foreground text-xs">
            {t("jobId")} <code className="bg-muted rounded px-1">{jobId}</code>
          </p>
        )}
      </CardContent>
    </Card>
  )
}
